import * as XLSX from "xlsx";

export const ROW_STATUS = {
  READY: "ready",
  INVALID: "invalid",
  SKIPPED: "skipped",
  CREATED: "created",
  FAILED: "failed",
};

const normalizeHeader = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201a\u201b\u2032\u2035`´']/g, "'")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const HEADER_RULES = {
  name: {
    aliases: ["name", "nom"],
    keywords: ["nom", "name"],
    exclude: ["reference", "identification", "adresse", "address"],
  },
  clientUniqueId: {
    aliases: [
      "clientuniqueid",
      "reference",
      "reference d'identification",
      "identification reference",
      "ref identification",
      "id reference",
    ],
    keywords: ["reference", "identification", "clientuniqueid"],
    exclude: ["adresse", "address"],
  },
  address: {
    aliases: ["address", "adresse"],
    keywords: ["adresse", "address"],
    exclude: ["reference", "identification"],
  },
};

const matchesHeaderRule = (normalizedHeader, rule) => {
  if (!normalizedHeader) return false;
  if (rule.exclude?.some((term) => normalizedHeader.includes(term))) {
    return false;
  }
  if (rule.aliases.includes(normalizedHeader)) return true;
  return rule.keywords.some((keyword) => normalizedHeader.includes(keyword));
};

const resolveHeaderMap = (headers = []) => {
  const normalizedHeaders = headers.map((header) => normalizeHeader(header));
  const map = {};
  const usedIndices = new Set();

  ["name", "clientUniqueId", "address"].forEach((targetKey) => {
    const rule = HEADER_RULES[targetKey];
    const index = normalizedHeaders.findIndex(
      (header, headerIndex) =>
        !usedIndices.has(headerIndex) && matchesHeaderRule(header, rule),
    );
    if (index >= 0) {
      map[targetKey] = headers[index];
      usedIndices.add(index);
    }
  });

  return map;
};

export const parseClientImportFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const jsonRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
  const headers = jsonRows.length > 0 ? Object.keys(jsonRows[0]) : [];
  const headerMap = resolveHeaderMap(headers);

  if (!headerMap.name) {
    throw new Error("missing-columns");
  }

  const mappedRows = jsonRows.map((row, index) => ({
    line: index + 2,
    name: String(row[headerMap.name] || "").trim(),
    clientUniqueId: headerMap.clientUniqueId
      ? String(row[headerMap.clientUniqueId] || "").trim()
      : "",
    address: headerMap.address
      ? String(row[headerMap.address] || "").trim()
      : "",
  }));

  const rows = mappedRows.filter(
    (row) => row.name || row.clientUniqueId || row.address,
  );

  return { rows, headerMap };
};

const normalizeValue = (value) => String(value ?? "").trim();

export const validateImportRows = (rows, existingClients = []) => {
  const existingNames = new Set(
    existingClients
      .map((client) => normalizeValue(client.name).toLowerCase())
      .filter(Boolean),
  );
  const existingReferences = new Set(
    existingClients
      .map((client) => normalizeValue(client.clientUniqueId).toLowerCase())
      .filter(Boolean),
  );

  const seenNames = new Set();
  const seenReferences = new Set();

  return rows.map((row) => {
    const name = normalizeValue(row.name);
    const clientUniqueId = normalizeValue(row.clientUniqueId);
    const address = normalizeValue(row.address);
    const issueCodes = [];

    if (!name) {
      return {
        ...row,
        name,
        clientUniqueId,
        address,
        status: ROW_STATUS.INVALID,
        issueCodes: ["missing_required"],
      };
    }

    const normalizedName = name.toLowerCase();
    let status = ROW_STATUS.READY;

    if (existingNames.has(normalizedName) || seenNames.has(normalizedName)) {
      status = ROW_STATUS.SKIPPED;
      issueCodes.push("duplicate_name");
    }

    if (clientUniqueId) {
      const normalizedReference = clientUniqueId.toLowerCase();
      if (
        existingReferences.has(normalizedReference) ||
        seenReferences.has(normalizedReference)
      ) {
        status = ROW_STATUS.SKIPPED;
        issueCodes.push("duplicate_reference");
      }
      seenReferences.add(normalizedReference);
    }

    seenNames.add(normalizedName);

    return {
      ...row,
      name,
      clientUniqueId,
      address,
      status,
      issueCodes,
    };
  });
};

const lineKey = (line) => Number(line);

export const mergeImportReport = (rows, report) => {
  if (!report) return rows;

  const statusByLine = new Map();
  (report.results || []).forEach((item) => {
    statusByLine.set(lineKey(item.line), item);
  });
  (report.failed || []).forEach((item) => {
    const key = lineKey(item.line);
    if (!statusByLine.has(key)) statusByLine.set(key, item);
  });
  (report.skipped || []).forEach((item) => {
    const key = lineKey(item.line);
    if (!statusByLine.has(key)) statusByLine.set(key, item);
  });

  return rows.map((row) => {
    const result = statusByLine.get(lineKey(row.line));
    if (!result) {
      return row;
    }

    if (result.status === "created") {
      return { ...row, status: ROW_STATUS.CREATED, issueCodes: [] };
    }
    if (result.status === "skipped" || result.code?.startsWith("duplicate")) {
      return {
        ...row,
        status: ROW_STATUS.SKIPPED,
        issueCodes: [result.code || "duplicate_name"],
      };
    }
    return {
      ...row,
      status: ROW_STATUS.FAILED,
      issueCodes: result.missingFields
        ? ["missing_required"]
        : [result.code || "failed"],
    };
  });
};

export const countRowsByStatus = (rows) =>
  rows.reduce((acc, row) => {
    acc[row.status] = (acc[row.status] || 0) + 1;
    return acc;
  }, {});

export const getRowsToImport = (rows) =>
  rows.filter((row) => row.status === ROW_STATUS.READY);
